import { useState } from "react"
import { Section, CodeBlock, InfoBox } from "@/components"
import { Zap, Settings } from "lucide-react"
import { cn } from "@/lib/utils"

export function MongoDBQuickStartPage() {
  const [setupMode, setSetupMode] = useState<"auto" | "manual">("auto")

  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Quick Start</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Get the MongoDB template running in minutes with our automatic setup script, 
          or follow the manual installation steps for more control.
        </p>
      </div>

      <div className="space-y-8">
        <div className="flex gap-4 p-2 bg-muted/30 rounded-xl">
          <button
            onClick={() => setSetupMode("auto")}
            className={cn(
              "flex-1 flex items-center justify-center gap-3 px-8 py-5 rounded-lg font-semibold text-lg transition-all",
              setupMode === "auto"
                ? "bg-primary text-primary-foreground shadow-lg"
                : "bg-transparent hover:bg-muted"
            )}
          >
            <Zap className="h-6 w-6" />
            Automatic Setup
          </button>
          <button
            onClick={() => setSetupMode("manual")}
            className={cn(
              "flex-1 flex items-center justify-center gap-3 px-8 py-5 rounded-lg font-semibold text-lg transition-all",
              setupMode === "manual"
                ? "bg-primary text-primary-foreground shadow-lg"
                : "bg-transparent hover:bg-muted"
            )}
          >
            <Settings className="h-6 w-6" />
            Manual Setup
          </button>
        </div>
        
        {setupMode === "auto" && (
          <div className="space-y-6 mt-6">
          <InfoBox type="info" title="Recommended">
            The setup script will guide you through installation, configuration, and environment setup.
          </InfoBox>
          <CodeBlock
            code={`# Clone the repository
git clone https://github.com/ALIRAZA47/template-nestjs-mongodb.git
cd template-nestjs-mongodb

# Run the setup script
npm run setup

# The script will:
# - Install dependencies
# - Configure MongoDB connection
# - Generate JWT secrets
# - Create .env file

# Start development
npm run start:dev`}
            language="bash"
          />
          <p className="text-sm text-muted-foreground">
            The application will be available at <code className="bg-muted px-2 py-1 rounded">http://localhost:3000</code>
          </p>
          </div>
        )}
        
        {setupMode === "manual" && (
          <div className="space-y-6 mt-6">
          <div className="space-y-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">1. Clone and Install</h3>
              <CodeBlock
                code={`git clone https://github.com/ALIRAZA47/template-nestjs-mongodb.git
cd template-nestjs-mongodb
npm install`}
                language="bash"
              />
            </div>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">2. Configure Environment</h3>
              <CodeBlock
                code={`cp env.sample .env
# Edit .env and configure your settings`}
                language="bash"
              />
            </div>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">3. Generate JWT Secrets</h3>
              <CodeBlock
                code={`node -e "console.log(require('crypto').randomBytes(64).toString('hex'))"`}
                language="bash"
              />
              <p className="text-sm text-muted-foreground">
                Run this twice to generate both JWT_SECRET and JWT_REFRESH_SECRET
              </p>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">4. Start Application</h3>
              <CodeBlock
                code={`npm run start:dev`}
                language="bash"
              />
            </div>
          </div>
          </div>
        )}
      </div>

      <Section title="What's Next?" description="After installation">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Access Your Application</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span><strong>API:</strong> http://localhost:3000</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span><strong>Swagger:</strong> http://localhost:3000/api/docs</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span><strong>Scalar:</strong> http://localhost:3000/api/docs/scalar</span>
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Explore the Template</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span>Check out the project structure</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span>Configure OAuth providers</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">→</span>
                <span>Review the authentication system</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      <InfoBox type="success" title="You're all set!">
        Your NestJS MongoDB application is now running. Visit the API documentation to explore 
        available endpoints and start building your features.
      </InfoBox>
    </div>
  )
}

